<template>
    <div>
        <SellersHeaderV2
            title="Vendedores"
            subtitle="Vendedores da sua rede, com cotações e participação"
            :filters="filters"
            :appliedFilters="appliedFilters"
            :states="states"
            :isDesktop="isDesktop"
            :showTotalizadores="showTotalizadores"
            @filtrar="filtrar"
            @limpar="handleLimparFiltros"
            @reload="loadData()"
            @toggle-totalizadores="showTotalizadores = !showTotalizadores"
        />

        <div v-if="isDesktop" class="page-section">
            <dashboard-stats
                :items="dashboardCards"
                :showTotalizadores="showTotalizadores"
                :selectedKeys="selectedCardKeys"
                dynamic-width
                @click="aplicarFiltroRapido"
            />
        </div>

        <div class="page-section">
            <a-card :bordered="false" style="width: 100%" class="table-card">
                <div class="table-container">
                    <a-table
                        size="small"
                        :columns="columns"
                        :data-source="sellers"
                        row-key="id"
                        :loading="loading"
                        :pagination="pagination"
                        :scroll="{ x: 'max-content' }"
                        :locale="{ emptyText: 'Nenhum vendedor encontrado' }"
                        @change="handleTableChange"
                    >
                        <template slot="id" slot-scope="text">#{{ text }}</template>
                        <template slot="name" slot-scope="text, record">
                            <div>{{ record.name }}</div>
                            <div v-if="record.provider" class="secondary-text">{{ record.provider.name }}</div>
                        </template>
                        <template slot="contact" slot-scope="text, record">
                            <div>{{ record.email }}</div>
                            <a v-if="record.phone" :href="'https://api.whatsapp.com/send?phone=' + formatNumber(record.phone)" target="_blank" class="phone-link">
                                <i class="fab fa-whatsapp"></i>{{ record.phone | VMask("(##) ####-#####") }}
                            </a>
                            <span v-else class="secondary-text">--</span>
                        </template>
                        <template slot="participation" slot-scope="text, record">
                            <a-tag v-if="getParticipation(record) > 0" :color="participationColor(getParticipation(record))">
                                {{ (getParticipation(record) * 100).toFixed(2) }} %
                            </a-tag>
                            <span v-else>--</span>
                        </template>
                        <template slot="acoes" slot-scope="text, record">
                            <ActionMenu :actions="getActions(record)" />
                        </template>
                    </a-table>
                </div>
            </a-card>
        </div>
    </div>
</template>

<script>
import { loaderMixin } from "@/mixins/sweet-loader.mixin";
import ErrorHandlerService from "@/services/ErrorHandlerService";
import StatsCards from "@/components/cliente/monitors/StatsCards.vue";
import ActionMenu from "@/components/popovers/action-menu.popover.vue";
import SellersHeaderV2 from "../SellersHeaderV2.vue";
import { sellersListMixin } from "../sellers-list.mixin";

const SORT_PARAMS = {
    cotacao_count: "orderCount",
    participation: "participation_order",
};

export default {
    name: "SellersV2Tab",
    mixins: [loaderMixin, sellersListMixin],
    components: {
        "dashboard-stats": StatsCards,
        ActionMenu,
        SellersHeaderV2,
    },
    data() {
        return {
            sellers: [],
            sort: { columnKey: undefined, order: undefined },
        };
    },
    watch: {
        "pagination.total"(value) {
            this.$emit("count", value);
        },
    },
    computed: {
        columns() {
            return [
                { title: "Id", dataIndex: "id", key: "id", scopedSlots: { customRender: "id" } },
                { title: "Nome do vendedor", key: "name", scopedSlots: { customRender: "name" } },
                { title: "Email/Telefone", key: "contact", scopedSlots: { customRender: "contact" } },
                {
                    title: "Cotações",
                    dataIndex: "cotacao_count",
                    key: "cotacao_count",
                    align: "center",
                    sorter: true,
                    sortDirections: ["descend", "ascend"],
                    sortOrder: this.sortOrderFor("cotacao_count"),
                },
                {
                    title: "Participação",
                    key: "participation",
                    align: "center",
                    sorter: true,
                    sortDirections: ["descend", "ascend"],
                    sortOrder: this.sortOrderFor("participation"),
                    scopedSlots: { customRender: "participation" },
                },
                { title: "Ação", key: "acoes", align: "center", width: 70, scopedSlots: { customRender: "acoes" } },
            ];
        },
    },
    methods: {
        sortOrderFor(columnKey) {
            return this.sort.columnKey === columnKey && this.sort.order ? this.sort.order : false;
        },
        resetSort() {
            this.sort = { columnKey: undefined, order: undefined };
        },
        loadData(page = this.pagination.current) {
            this.loading = true;
            const params = { ...this.buildParams(page), seller_order: true };
            const sortParam = SORT_PARAMS[this.sort.columnKey];
            if (sortParam && this.sort.order) params[sortParam] = this.sort.order === "ascend" ? "asc" : "desc";
            this.svc
                .load(params)
                .then(({ data }) => {
                    this.sellers = data.data || [];
                    this.pagination.current = page;
                    this.readMeta(data.meta);
                })
                .catch((error) => {
                    ErrorHandlerService.handle(error, this.$store);
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        handleTableChange(pagination, filters, sorter) {
            const next = { columnKey: sorter && sorter.order ? sorter.columnKey : undefined, order: sorter ? sorter.order : undefined };
            if (next.columnKey !== this.sort.columnKey || next.order !== this.sort.order) {
                this.sort = next;
                this.pagination.pageSize = pagination.pageSize;
                this.loadData(1);
                return;
            }
            this.pagination.pageSize = pagination.pageSize;
            this.loadData(pagination.current);
        },
        getActions(seller) {
            return [{ label: "Excluir vendedor", icon: "delete", iconClass: "action-danger", onClick: () => this.removeSeller(seller) }];
        },
        removeSeller(seller) {
            this.confirm_action({
                message: `Confirma remoção do vendedor ${seller.name}?`,
                callback: () =>
                    this.prov_svc.remove_seller(seller.id).then(() => {
                        this.loadData();
                    }),
            });
        },
    },
};
</script>

<style lang="scss" scoped>
@import "@/sass/commons/_list-page-v2";

.secondary-text {
    color: #8c8c8c;
}
.phone-link {
    white-space: nowrap;
    color: #30aa4c;
}
.phone-link .fab {
    margin-right: 6px;
}
</style>
